import React, { useEffect, useState } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader2, Tag, ArrowRight, ShieldCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import { useAuth } from '../contexts/AuthContext';
import { useSEO } from '../utils/seo';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Cart Recovery landing.
 *
 * Lands here from the WhatsApp / email "Resume my order" button after the
 * 50%-off cart-recovery cron fires. Reads `?tx=…&code=COMEBACK50` from
 * the URL, looks up the original transaction, and resumes the user back
 * to their abandoned checkout with the discount auto-applied.
 *
 * Logged-out users get a "Log in to redeem" gate instead of being silently
 * bounced to /signup (the old failure mode that triggered the bug report).
 */
const CartRecoveryPage = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [tx, setTx] = useState(null);
  const [error, setError] = useState(null);

  const sessionId = params.get('tx') || '';
  const code = (params.get('code') || 'COMEBACK50').toUpperCase();

  useSEO({
    title: 'Resume your Shopperden order — 50% off',
    description: 'Pick up where you left off. Apply your one-off 50% discount and finish your Shopperden order.',
    canonical: 'https://shopperden.com/cart-recovery',
  });

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/api/payments/cart-recovery/lookup`, {
          params: { tx: sessionId },
        });
        if (!cancelled) setTx(r.data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || 'Recovery link expired or invalid.');
      }
    })();
    return () => { cancelled = true; };
  }, [sessionId]);

  const handleResume = async () => {
    if (!user) {
      toast.message('Sign in to apply your discount', { description: 'Your 50% off will be ready after login.' });
      navigate(`/login?next=${encodeURIComponent(window.location.pathname + window.location.search)}`);
      return;
    }
    setLoading(true);
    try {
      const r = await axios.post(`${API_URL}/api/payments/cart-recovery/resume`, {
        tx: sessionId, code,
      });
      if (r.data?.url) {
        window.location.href = r.data.url;
      } else {
        toast.error('Unable to rebuild your checkout. Try browsing again from your dashboard.');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not resume your order');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Header />
      <div className="max-w-2xl mx-auto px-4 py-16">
        <div className="bg-white border border-slate-200 rounded-md p-8 sm:p-10" data-testid="cart-recovery-page">
          <p className="text-[10px] tracking-[0.3em] text-[#E07A3A] uppercase font-semibold mb-2">One-off discount</p>
          <h1 className="font-['Playfair_Display'] text-3xl sm:text-4xl text-[#0F172A] mb-3">
            Pick up where you left off
          </h1>
          <p className="text-slate-600 mb-6 leading-relaxed">
            We saved your match. Apply the 50% discount below to finish your order.
          </p>

          <div className="bg-[#FEF3C7] border border-[#FDE68A] rounded p-5 text-center mb-6" data-testid="recovery-code-block">
            <Tag className="h-5 w-5 text-[#A16207] mx-auto mb-2" />
            <div className="text-[10px] text-[#A16207] uppercase tracking-[0.2em] font-semibold">50% off · one time</div>
            <div className="text-3xl font-bold text-[#A16207] tracking-[0.2em] my-1">{code}</div>
            {tx?.amount && (
              <div className="text-sm text-[#92400E]">
                Was {tx.currency?.toUpperCase()} {tx.amount?.toLocaleString()} → <b>{tx.currency?.toUpperCase()} {(tx.amount * 0.5).toLocaleString()}</b>
              </div>
            )}
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded p-3 mb-4" data-testid="recovery-error">
              {error}
            </div>
          )}

          <Button
            onClick={handleResume}
            disabled={loading || !sessionId}
            className="w-full bg-[#0F172A] hover:bg-[#1E293B] text-white tracking-widest text-sm uppercase font-semibold h-12"
            data-testid="resume-order-btn"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Resume my order with {code}
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>

          {!user && (
            <p className="text-xs text-slate-500 mt-3 text-center">
              You'll be asked to log in first — your 50% off stays applied.
            </p>
          )}

          <div className="flex items-center gap-2 text-[11px] text-slate-400 mt-6 pt-4 border-t border-slate-100">
            <ShieldCheck className="h-3.5 w-3.5" />
            Code expires in 24 hours. Secure checkout via Stripe.
          </div>

          <div className="mt-4 text-center">
            <Link to="/properties" className="text-xs text-slate-500 hover:text-[#E07A3A] underline">
              Browse properties instead
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartRecoveryPage;
